/**
 * The Kestrel lease (spec §9.2, D-030; plan §6 M2-06): the due-time cycle with automatic payment wherever the
 * player is, missed payments with late fees and a permanent rate step, collections (`notice`, then `repo`), the
 * repo pause after a defeated repo crew, extra payments and arrears at the Lease Office, and ownership.
 *
 * Every command is pure (the input career is never changed) and returns a `CmdResult`. Money is kept in whole
 * credits: capitalised interest is rounded to the nearest credit.
 *
 * Arrears accounting (D-TBD: arrears accounting and post-ownership insurance):
 * - A due payment is `min(payment, principal)`, so the last payment never overcharges.
 * - A missed payment adds that amount to `arrears` (a part of the principal that is overdue) and `lateFee` to
 *   `feesDue`; the principal is unchanged. Paying the arrears pays `min(arrears, principal) + feesDue` and takes
 *   the arrears part off the principal.
 * - A successful automatic payment does not clear arrears; only `payArrears` (or ownership) does.
 * - Collections are judged at each due time against that due time (so a multi-cycle catch-up is exact):
 *   `notice` from the first miss, `repo` from `repoAfterMissed` misses unless the repo pause covers that due time.
 * - Ownership (principal ≤ 0 anywhere) clears arrears, misses and collections, resets the rate, and rolls any
 *   unpaid late fees into the interest-free `debt`.
 * - After ownership each due time charges the insurance premium when insured (a shortfall becomes debt) and then
 *   repays debt up to `debtPaymentPerCycle` from the wallet.
 */
import type { M2Data } from '../data/m2Schemas'
import { EPSILON } from './ops'
import { fail, insufficientFunds, invalidAmount, ok, type CareerEffect, type CmdResult, type CmdSuccess } from './result'
import type { Career, Collections, LeaseEvent, LeaseEventKind, LeaseState } from './types'

type LeaseData = Pick<M2Data, 'lease'>

/** Mutable working copy of the money a command touches; `commit` folds it back into a new Career. */
interface Draft {
  wallet: number
  debt: number
  lease: LeaseState
  effects: CareerEffect[]
}

function draftOf(career: Career): Draft {
  return { wallet: career.wallet, debt: career.debt, lease: { ...career.lease, history: [...career.lease.history] }, effects: [] }
}

function commit(career: Career, d: Draft, data: LeaseData): Career {
  const history = d.lease.history
  const limit = data.lease.historyLimit
  if (history.length > limit) history.splice(0, history.length - limit)
  return { ...career, wallet: settle(d.wallet), debt: settle(d.debt), lease: d.lease }
}

/** Round-off within EPSILON settles at 0 (money never goes negative). */
function settle(v: number): number {
  return v < EPSILON ? 0 : v
}

function log(d: Draft, t: number, kind: LeaseEventKind, amount: number): void {
  const event: LeaseEvent = { t, kind, amount: amount > 0 ? amount : 0 }
  d.lease.history.push(event)
}

function setCollections(d: Draft, to: Collections): void {
  const from = d.lease.collections
  if (from === to) return
  d.lease.collections = to
  d.effects.push({ kind: 'collectionsChanged', from, to })
}

/** Ownership (once): principal 0, collections cleared, rate reset, insurance off (optional from now on). */
function becomeOwned(d: Draft, data: LeaseData, t: number): void {
  const lease = d.lease
  if (lease.owned) return
  lease.principal = 0
  lease.owned = true
  lease.insured = false
  lease.ratePerCycle = data.lease.ratePerCycle
  lease.missed = 0
  lease.arrears = 0
  if (lease.feesDue > EPSILON) {
    d.debt += lease.feesDue
    log(d, t, 'debtIncurred', lease.feesDue)
    d.effects.push({ kind: 'debtIncurred', amount: lease.feesDue, debt: d.debt })
  }
  lease.feesDue = 0
  setCollections(d, 'none')
  log(d, t, 'ownership', 0)
  d.effects.push({ kind: 'ownership', gameTime: t })
}

function checkOwnership(d: Draft, data: LeaseData, t: number): void {
  if (!d.lease.owned && d.lease.principal <= EPSILON) becomeOwned(d, data, t)
}

/** One due time before ownership: interest, then the automatic payment or a miss. */
function leaseCycle(d: Draft, data: LeaseData, due: number): void {
  const cfg = data.lease
  const lease = d.lease
  const interest = Math.round(lease.principal * lease.ratePerCycle)
  if (interest > 0) {
    lease.principal += interest
    log(d, due, 'interest', interest)
  }
  const amount = Math.min(lease.payment, lease.principal)
  if (d.wallet + EPSILON >= amount) {
    d.wallet -= amount
    lease.principal -= amount
    log(d, due, 'paid', amount)
    d.effects.push({ kind: 'leasePaid', amount, principal: lease.principal > EPSILON ? lease.principal : 0 })
    checkOwnership(d, data, due)
    return
  }
  lease.missed += 1
  const overdue = Math.max(0, Math.min(amount, lease.principal - lease.arrears))
  lease.arrears += overdue
  lease.feesDue += cfg.lateFee
  lease.ratePerCycle = Math.min(lease.ratePerCycle + cfg.rateStepOnMiss, cfg.rateCap)
  log(d, due, 'missed', amount)
  if (cfg.lateFee > 0) log(d, due, 'lateFee', cfg.lateFee)
  d.effects.push({ kind: 'paymentMissed', missed: lease.missed, arrears: lease.arrears, lateFee: cfg.lateFee })
  const paused = lease.repoPausedUntil > due
  if (lease.missed >= cfg.repoAfterMissed && !paused) {
    setCollections(d, 'repo')
    d.effects.push({ kind: 'repoTriggered' })
  } else if (lease.collections === 'none') {
    setCollections(d, 'notice')
  }
}

/** One due time after ownership: the insurance premium (shortfall → debt), then debt repayment. */
function ownedCycle(d: Draft, data: LeaseData, due: number): void {
  const cfg = data.lease
  if (d.lease.insured && cfg.insurance.premiumPerCycle > 0) {
    const premium = cfg.insurance.premiumPerCycle
    const paid = Math.min(premium, d.wallet)
    d.wallet -= paid
    log(d, due, 'premium', premium)
    d.effects.push({ kind: 'premiumCharged', amount: premium })
    const shortfall = premium - paid
    if (shortfall > EPSILON) {
      d.debt += shortfall
      log(d, due, 'debtIncurred', shortfall)
      d.effects.push({ kind: 'debtIncurred', amount: shortfall, debt: d.debt })
      return
    }
  }
  const repay = Math.min(d.debt, cfg.debtPaymentPerCycle, d.wallet)
  if (repay > EPSILON) {
    d.wallet -= repay
    d.debt -= repay
    log(d, due, 'debtPaid', repay)
  }
}

/**
 * Settles every due time up to `now` (a long absence settles each missed cycle in order). Always succeeds; the
 * effects list the payments, misses, collections changes, repo triggers, premiums and ownership in order.
 * `gameTime` is not changed (the runtime folds the clock separately).
 */
export function processDue(career: Career, data: LeaseData, now: number): CmdSuccess<Career> {
  if (!(now >= career.lease.nextDueAt)) return ok(career)
  const d = draftOf(career)
  const cycle = d.lease.cycleSeconds
  while (now >= d.lease.nextDueAt) {
    const due = d.lease.nextDueAt
    if (d.lease.owned) ownedCycle(d, data, due)
    else leaseCycle(d, data, due)
    d.lease.nextDueAt = due + cycle
  }
  return ok(commit(career, d, data), d.effects)
}

function ownedRefusal(): CmdResult<Career> {
  return fail('leaseOwned', 'The Kestrel is already yours: there is no lease left to pay.')
}

/** Pays `amount` off the principal (Lease Office at the Hub; the caller checks the station). Capped at the principal. */
export function payExtra(career: Career, data: LeaseData, amount: number): CmdResult<Career> {
  if (career.lease.owned) return ownedRefusal()
  if (!Number.isFinite(amount) || amount <= 0) return invalidAmount('payment', amount)
  const pay = Math.min(amount, career.lease.principal)
  if (career.wallet + EPSILON < pay) return insufficientFunds(pay, career.wallet)
  const d = draftOf(career)
  d.wallet -= pay
  d.lease.principal -= pay
  if (d.lease.arrears > d.lease.principal) d.lease.arrears = Math.max(0, d.lease.principal)
  log(d, career.gameTime, 'extra', pay)
  checkOwnership(d, data, career.gameTime)
  return ok(commit(career, d, data), d.effects)
}

/** What clearing the arrears costs now: the overdue payments (at most the principal) plus the late fees. */
export function arrearsDue(career: Career): number {
  return Math.min(career.lease.arrears, career.lease.principal) + career.lease.feesDue
}

/** Pays the arrears and late fees: misses reset, collections back to `none` (the raised rate stays). */
export function payArrears(career: Career, data: LeaseData): CmdResult<Career> {
  if (career.lease.owned) return ownedRefusal()
  const overdue = Math.min(career.lease.arrears, career.lease.principal)
  const total = overdue + career.lease.feesDue
  if (total <= EPSILON && career.lease.missed === 0) {
    return fail('invalidAmount', 'There are no arrears to pay.', { amount: 0 })
  }
  if (career.wallet + EPSILON < total) return insufficientFunds(total, career.wallet)
  const d = draftOf(career)
  d.wallet -= total
  d.lease.principal -= overdue
  d.lease.arrears = 0
  d.lease.feesDue = 0
  d.lease.missed = 0
  log(d, career.gameTime, 'arrearsPaid', total)
  setCollections(d, 'none')
  checkOwnership(d, data, career.gameTime)
  return ok(commit(career, d, data), d.effects)
}

/** The game time the repo pause would run to if a repo crew were defeated at `now`: the next due time plus a cycle. */
export function repoPauseEnd(lease: Pick<LeaseState, 'nextDueAt' | 'cycleSeconds'>, now: number): number {
  let due = lease.nextDueAt
  if (now >= due) due += Math.floor((now - due) / lease.cycleSeconds + 1) * lease.cycleSeconds
  return due + lease.cycleSeconds
}

/** A repo crew was defeated: collections pause (no repo crew, no new `repo`) until the next due time plus one cycle. */
export function repoDefeated(career: Career, now: number): CmdSuccess<Career> {
  const until = repoPauseEnd(career.lease, now)
  if (until <= career.lease.repoPausedUntil) return ok(career)
  return ok({ ...career, lease: { ...career.lease, repoPausedUntil: until } })
}

/** Whether collections are paused at `now` (the career mirror's `repoPaused`). */
export function isRepoPaused(career: Career, now: number): boolean {
  return career.lease.repoPausedUntil > now
}

/** Turns post-ownership insurance on or off (the premium is charged at each due time while on). */
export function setInsurance(career: Career, on: boolean): CmdResult<Career> {
  if (!career.lease.owned) {
    return fail('serviceUnavailable', 'Insurance is offered once the Kestrel is yours; until then the lease covers the hull.')
  }
  if (career.lease.insured === on) return ok(career)
  return ok({ ...career, lease: { ...career.lease, insured: on } })
}
